import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import { LayoutAlternative } from '@thive/fddm-types';

import { DeleteIcon, DownloadIcon, GridViewIcon, PlusIcon } from '@/assets/icons';
import { queryItems } from '@/modules/common/helpers/search';
import { StyledTooltip } from '@common/components/StyledTooltip';
import { TextInput } from '@common/components/inputs';
import {
  UserPreferenceName,
  getUserPreference,
} from '@/modules/userPreferences';
import {
  useUserPreference,
  useUpdateUserPreferences,
} from '@/modules/userPreferences/hooks';

import { FloorplanGrid } from './FloorplanGrid';
import { FloorplanImporter } from './FloorplanImporter';
import { FloorplanList } from './FloorplanList';
import { SortingMenu } from './sortingMenu/SortingMenu';
import { useFloorplanAdminStore } from './store';
import {
  DEFAULT_SORT_ORDER_OPTIONS,
  FloorplanViewMode,
  SortingCriteria,
  gridViewEnabledSortingCriteria,
} from './sortingMenu/constants';
import { calcSortedAlternatives } from './helpers/floorplans';
import { useShallow } from 'zustand/react/shallow';

type Props = {
  downloadIsDisabled: boolean;
  downloadSelectedFloorplan: () => void;
  openCreator: () => void;
  openDeleter: () => void;
  openDuplicator: () => void;
};

export const FloorplanLayout = ({
  downloadIsDisabled,
  downloadSelectedFloorplan,
  openCreator,
  openDeleter,
  openDuplicator,
}: Props) => {
  const { t } = useTranslation();
  const { selectedAlternativeIds, setSelectedAlternativeIds, allAlternatives } =
    useFloorplanAdminStore(
      useShallow((state) => ({
        selectedAlternativeIds: state.selectedFloorplanIdsState,
        setSelectedAlternativeIds: state.setSelectedFloorplanIdsState,
        allAlternatives: state.floorPlanGroups,
      })),
    );
  const floorplanView = useUserPreference(
    UserPreferenceName.FLOORPLAN_ADMIN_VIEW_MODE,
  );
  const sortingCriteria = useUserPreference(
    UserPreferenceName.FLOORPLAN_ADMIN_SORT_BY,
  );
  const sortOrder = useUserPreference(
    UserPreferenceName.FLOORPLAN_ADMIN_SORT_ORDER,
  );
  const { updateUserPreference } = useUpdateUserPreferences();
  const [sortedAlternatives, setSortedAlternatives] = useState<
    LayoutAlternative[]
  >([]);
  const [alternativesHits, setAlternativesHits] = useState<LayoutAlternative[]>(
    [],
  );

  const { search, setSearch } = useFloorplanAdminStore(
    useShallow((state) => ({
      search: state.searchState,
      setSearch: state.setSearchState,
    })),
  );

  useEffect(() => {
    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSortedAlternatives(calcSortedAlternatives(allAlternatives, sortingCriteria, sortOrder));
  }, [allAlternatives, sortingCriteria, sortOrder]);

  useEffect(() => {
    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAlternativesHits(
      queryItems(search, sortedAlternatives, (item) => item.name),
    );
  }, [sortedAlternatives, search]);

  const switchToGridView = useCallback(() => {
    const currentSortCriteria = getUserPreference(
      UserPreferenceName.FLOORPLAN_ADMIN_SORT_BY,
    );
    if (!gridViewEnabledSortingCriteria.includes(currentSortCriteria)) {
      // NOTE: patch unsupported sorting criteria in grid view
      const overrideCriteria = SortingCriteria.NAME;
      updateUserPreference(
        UserPreferenceName.FLOORPLAN_ADMIN_SORT_BY,
        overrideCriteria,
        false,
      );
      updateUserPreference(
        UserPreferenceName.FLOORPLAN_ADMIN_SORT_ORDER,
        DEFAULT_SORT_ORDER_OPTIONS[overrideCriteria],
        false,
      );
    }

    updateUserPreference(
      UserPreferenceName.FLOORPLAN_ADMIN_VIEW_MODE,
      FloorplanViewMode.GRID,
    );
  }, [updateUserPreference]);

  const switchToListView = useCallback(() => {
    updateUserPreference(
      UserPreferenceName.FLOORPLAN_ADMIN_VIEW_MODE,
      FloorplanViewMode.LIST,
    );
  }, [updateUserPreference]);

  const selectAllFloorplans = useCallback(() => {
    if (selectedAlternativeIds.length !== alternativesHits.length)
      setSelectedAlternativeIds(
        alternativesHits.map((item) => item.alternativeId),
      );
    else setSelectedAlternativeIds([]);
  }, [alternativesHits, setSelectedAlternativeIds, selectedAlternativeIds]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        flexGrow: 1,
      }}
    >
      <Container maxWidth='xl'>
        <Stack direction='row' justifyContent='space-between' alignItems='center' mt={5} mb={3}>
          {selectedAlternativeIds.length > 0 ? (
            <Stack direction='row' gap={1} alignItems='center'>
              <StyledTooltip
                placement='bottom'
                title={
                  <Trans
                    i18nKey='select_all_floorplans.tooltip'
                    ns='interface'
                    components={{
                      s: <span />,
                    }}
                  >
                    Select all floorplans
                  </Trans>
                }
              >
                <IconButton onClick={selectAllFloorplans}>
                  <IndeterminateCheckBoxOutlinedIcon />
                </IconButton>
              </StyledTooltip>
              <Typography align='left' variant='h4'>
                {selectedAlternativeIds.length} selected
              </Typography>

              <IconButton
                aria-label='download selected floorplan'
                disabled={downloadIsDisabled || selectedAlternativeIds.length !== 1}
                onClick={downloadSelectedFloorplan}
              >
                <DownloadIcon />
              </IconButton>

              <IconButton onClick={openDeleter} data-testid='delete-selected-floorplans'>
                <DeleteIcon />
              </IconButton>
            </Stack>
          ) : (
            <Typography align='left' variant='subtitle2'>
              {t('interface:home.your_floorplans', 'My floorplans')}
            </Typography>
          )}
          <TextInput
            placeholder={t('interface:home.search_floorplans', 'Search floorplans')}
            value={search}
            onChange={setSearch}
            prefix={<SearchIcon />}
          />
        </Stack>
        <Stack direction='row' justifyContent='space-between' alignItems='center' mb={4}>
          <Stack direction='row' gap={1} alignItems='center'>
            {floorplanView === 'grid' ? (
              <SortingMenu />
            ) : (
              <Button onClick={openCreator} variant='outlined' sx={{ py: 1 }}>
                <Stack direction='row' gap={1} alignItems='center'>
                  {t('interface:home.new_floorplan', 'New Floorplan')}
                  <PlusIcon sx={{ width: 25, height: 25 }} />
                </Stack>
              </Button>
            )}
            <FloorplanImporter />
          </Stack>
          <Stack direction='row' gap={1}>
            <IconButton
              onClick={switchToGridView}
              className={floorplanView === 'grid' ? 'active' : ''}
            >
              <GridViewIcon />
            </IconButton>
            <IconButton
              onClick={switchToListView}
              className={floorplanView === 'grid' ? '' : 'active'}
            >
              <ListIcon />
            </IconButton>
          </Stack>
        </Stack>

        {floorplanView === 'grid' ? (
          <FloorplanGrid
            alternatives={alternativesHits}
            onOpenCreatorClick={openCreator}
            openDuplicator={openDuplicator}
          />
        ) : (
          <FloorplanList alternatives={alternativesHits} />
        )}
      </Container>
    </Box>
  );
};
